import React, { useState } from "react";
import { useNavigate, useParams } from "react-router";
import AddIcon from "@mui/icons-material/Add";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import { Button } from "@/components/common/Button";
import { Card } from "@/components/common/Card";
import { OptionCard } from "@/components/common/OptionCard";
import { Radio } from "@/components/common/Radio";
import { TabToggle } from "@/components/common/TabToggle";
import { NumberCount } from "@/components/common/NumberCount";
import { StatusChip } from "@/components/common/StatusChip";
import { TextInput } from "@/components/common/TextInput";
import { Tooltip } from "@/components/common/Tooltip";
import { SectionRule } from "components/common/SectionRule";
import { IconPicker } from "components/common/IconPicker";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import {
  askingStep,
  createApp,
  lendTable,
  startApp,
  takeBackTable,
  updateApp,
} from "@/lib/api/apps/apps";
import {
  useApp,
  useAppAction,
  useApps,
  useDesignApp,
  useProviders,
} from "@/lib/api/apps/hooks";
import type {
  AppCadence,
  AppDetail,
  AppSetup,
  DesignApp,
  DesignSummary,
} from "@/lib/api/apps/types";
import { useQueryClient } from "@tanstack/react-query";
import { appKeys } from "@/lib/api/apps/keys";
import {
  WEEKDAYS,
  costLimitRefusal,
  searchesLine,
  statusLabel,
  usd,
  whenLabel,
} from "../app-format";
import { chooseModel } from "../design/draft-model";
import { ModelChip, useActiveOrgRef } from "../design/model-field";
import { InlineFigure, type FigureFormat } from "../limit-field";
import {
  AppPageHead,
  AppPageShell,
  Composer,
  NameTitle,
  NEW_APP_TRAIL,
  TitleInput,
} from "../page-shell";
import { ClarificationsPane } from "./asking-pane";
import { RunsTab } from "./runs-tab";
import {
  appliedTables,
  LentGraph,
  LentList,
  TablePicker,
  type LentTable,
} from "./table-picker";
import { RefusalCallout } from "@/components/authentication/consent/RefusalCallout";

type Step = "start" | "setup" | "questions" | "schedule" | "runs";

const STEPS: { step: Step; label: string }[] = [
  { step: "setup", label: "Setup" },
  { step: "questions", label: "Clarifications" },
  { step: "schedule", label: "Interval and limits" },
  { step: "runs", label: "Runs" },
];

const DEFAULT_TOTAL_CENTS = 2_000;
const DEFAULT_RUN_CAP_CENTS = 200;
const MAX_RUN_MINUTES = 120;

export function setupProblem(setup: {
  name: string;
  brief: string;
  lent: LentTable[];
  hasModel: boolean;
}): string | null {
  if (!setup.name.trim()) return "Needs a name";
  if (setup.lent.length === 0) return "Needs tables";
  if (!setup.lent.some((t) => t.access === "write"))
    return "Needs a table it writes";
  if (!setup.brief.trim()) return "Needs a brief";
  if (!setup.hasModel) return "Needs a model";
  return null;
}

export function runsProblem(runs: {
  cadence: AppCadence;
  days: string[];
  runCapCents: number;
  totalCents: number;
}): string | null {
  if (runs.runCapCents > runs.totalCents)
    return `A run may spend at most the app's ${usd(runs.totalCents)} total`;
  if (runs.cadence === "weekly" && runs.days.length === 0)
    return "Pick at least one day";
  return null;
}

const DAY_NAMES: Record<string, string> = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

/** "Every Monday and Thursday at 06:00, Europe/Berlin time." */
export function scheduleSentence(
  cadence: AppCadence,
  days: string[],
  time: string,
  zone: string,
): string {
  const at = `at ${time}, ${zone} time`;
  const named = days.map((d) => DAY_NAMES[d] ?? d);
  const list =
    named.length > 1
      ? `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`
      : (named[0] ?? "");
  switch (cadence) {
    case "once":
      return "Once, right after you start it. You can schedule it later.";
    case "daily":
      return `Every day ${at}.`;
    case "weekly":
      return named.length ? `Every ${list} ${at}.` : "Pick the days it runs.";
    case "monthly":
      return `On the 1st of every month ${at}.`;
  }
}

const MINUTES: FigureFormat = {
  show: (n) => `${n} min`,
  parse: (text) => {
    const n = Number(text.trim());
    return Number.isInteger(n) ? n : null;
  },
  edit: String,
  after: "min",
  inputMode: "numeric",
  unit: "in minutes",
  invalid: "Whole minutes",
};

const pad = (n: number) => String(n).padStart(2, "0");

const hhmm = (n: number) => `${pad(Math.floor(n / 60))}:${pad(n % 60)}`;

const CLOCK: FigureFormat = {
  show: hhmm,
  parse: (text) => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
    if (!m) return null;
    const [h, min] = [Number(m[1]), Number(m[2])];
    return h < 24 && min < 60 ? h * 60 + min : null;
  },
  edit: hhmm,
  inputMode: "numeric",
  invalid: "HH:MM",
};

/** The app's setup as the door takes it, `change` laid over. */
function setupOf(
  app: AppDetail | undefined,
  change: Partial<AppSetup>,
): AppSetup {
  return {
    name: app?.name ?? "",
    brief: app?.brief ?? "",
    cadence: app?.cadence ?? "once",
    days: app?.days ?? [],
    run_time: app?.run_time ?? "06:00",
    time_zone:
      app?.time_zone ??
      (Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"),
    run_cap_cents: app?.run_cap_cents ?? DEFAULT_RUN_CAP_CENTS,
    cost_limit_cents: app?.cost_limit_cents ?? DEFAULT_TOTAL_CENTS,
    run_minutes: app?.run_minutes ?? 20,
    policy: app?.policy ?? "keep_going",
    email_on_hold: app?.email_on_hold ?? true,
    provider: app?.provider ?? "house",
    model: app?.model ?? "",
    ...change,
  };
}

/** `/apps/new/research` and `/apps/:app_id` (docs/ResearchAppSetup.md): Setup,
 *  Clarifications, Interval and limits, then Runs, the same tabs while it is
 *  set up and once it runs. The app is made at Setup's Continue, because its
 *  questions read the tables as the app. */
export const ResearchAppPage: React.FC = () => {
  const { app_id } = useParams();
  const app = useApp(app_id ?? null);
  if (app_id && !app.data) {
    return (
      <AppPageShell maxWidth="45rem">
        <AppPageHead
          trail={[{ label: "Applications", to: "/apps" }]}
          title=""
          kind="Research"
        />
      </AppPageShell>
    );
  }
  return <SetupFlow key={app.data?.app_id ?? "new"} app={app.data} />;
};

function SetupFlow({ app }: { app?: AppDetail }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const orgId = useOrgContextStore((s) => s.activeOrgId);
  const org = useActiveOrgRef();
  const providers = useProviders(orgId);
  const [step, setStep] = useState<Step>(
    !app
      ? "start"
      : app.status !== "setup"
        ? "runs"
        : app.step === "ask"
          ? "questions"
          : "schedule",
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useAppAction(app?.app_id ?? "", "delete");
  const [name, setName] = useState(app?.name ?? "");
  const [placeholder, setPlaceholder] = useState("Untitled research app");
  const [brief, setBrief] = useState(app?.brief ?? "");
  const [total, setTotal] = useState(
    app?.cost_limit_cents ?? DEFAULT_TOTAL_CENTS,
  );
  const lentOf = (saved?: AppDetail): LentTable[] =>
    saved?.lent.map((l) => {
      const known = saved.tables.find((t) => t.table_id === l.table_id);
      return {
        table_id: l.table_id,
        name: known?.name ?? l.table_id,
        model: null,
        access: l.access,
      };
    }) ?? [];
  const [lent, setLent] = useState<LentTable[]>(() => lentOf(app));
  const [picking, setPicking] = useState(!app);
  const [tablesView, setTablesView] = useState<"list" | "graph">("list");
  const [picked, setPicked] = useState(
    app ? { provider: app.provider, model: app.model } : null,
  );
  const [modelMenu, setModelMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const choice = providers.data ? chooseModel(providers.data, picked) : null;
  const problem = setupProblem({
    name,
    brief,
    lent,
    hasModel: Boolean(choice),
  });
  const running = Boolean(app && app.status !== "setup");
  const lentKey = (rows: { table_id: string; access: string }[]) =>
    rows
      .map((l) => `${l.table_id}:${l.access}`)
      .sort()
      .join();
  const edited =
    app !== undefined &&
    (brief.trim() !== app.brief ||
      choice?.provider.id !== app.provider ||
      choice?.model.id !== app.model ||
      lentKey(lent) !== lentKey(app.lent));

  const act = async (work: () => Promise<void>) => {
    setBusy(true);
    setFailure(null);
    try {
      await work();
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
      void client.invalidateQueries({ queryKey: appKeys.all });
    }
  };

  const saveSetup = () =>
    act(async () => {
      if (!orgId || !choice) return;
      const setup = setupOf(app, {
        name: name.trim(),
        brief: brief.trim(),
        cost_limit_cents: total,
        run_cap_cents: Math.min(
          app?.run_cap_cents ?? DEFAULT_RUN_CAP_CENTS,
          total,
        ),
        provider: choice.provider.id,
        model: choice.model.id,
      });
      if (!app) {
        const made = await createApp({
          ...setup,
          org_id: orgId,
          tables: lent.map((t) => ({ table_id: t.table_id, access: t.access })),
        });
        navigate(`/apps/${made.app_id}`, { replace: true });
        return;
      }
      await updateApp(app.app_id, setup);
      const before = new Map(app.lent.map((l) => [l.table_id, l.access]));
      for (const t of lent)
        if (before.get(t.table_id) !== t.access)
          await lendTable(app.app_id, {
            table_id: t.table_id,
            access: t.access,
          });
      for (const id of before.keys())
        if (!lent.some((t) => t.table_id === id))
          await takeBackTable(app.app_id, id);
      if (app.status === "setup") {
        if (app.step !== "ask") await askingStep(app.app_id, { answers: [] });
        setStep("questions");
      }
    });

  const fromDesign = (design: DesignApp) => {
    setLent(
      appliedTables(design).map((t) => ({
        table_id: t.id,
        name: t.name,
        model: null,
        access: "write",
      })),
    );
    setBrief(design.brief);
    setPlaceholder(`${design.name} research`);
    setPicking(false);
    setStep("setup");
  };

  return (
    <AppPageShell maxWidth="45rem">
      <AppPageHead
        trail={app ? [{ label: "Applications", to: "/apps" }] : NEW_APP_TRAIL}
        kind="Research"
        onClose={() => navigate(app ? "/apps" : "/apps/new")}
        title={
          step === "start" ? (
            "New research app"
          ) : (
            <span className="flex items-center gap-3">
              {app ? (
                <NameTitle
                  name={app.name}
                  onRename={async (next) => {
                    await updateApp(app.app_id, setupOf(app, { name: next }));
                    setName(next);
                    void client.invalidateQueries({ queryKey: appKeys.all });
                  }}
                />
              ) : (
                <TitleInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={placeholder}
                  className="min-w-0 flex-1"
                />
              )}
              <span className="inline-flex items-center gap-1.5 text-fg-muted-regular-small">
                <Tooltip title="Total cost limit for the app, all runs (tokens + web lookups)">
                  <PaidOutlinedIcon
                    sx={{ fontSize: 14 }}
                    aria-label="Total cost limit"
                  />
                </Tooltip>
                {app && (
                  <span className="font-mono text-fg">
                    {usd(app.spent_total_cents)}
                  </span>
                )}
                <InlineFigure
                  value={total}
                  label="total cost limit"
                  prefix={app ? "of" : undefined}
                  refuse={(c) =>
                    costLimitRefusal(c, app?.spent_total_cents ?? 0)
                  }
                  onSave={async (c) => {
                    if (c === null) return;
                    // A running app keeps its limit at once; a new one, at Continue.
                    if (app && app.status !== "setup") {
                      await updateApp(
                        app.app_id,
                        setupOf(app, {
                          cost_limit_cents: c,
                          run_cap_cents: Math.min(app.run_cap_cents, c),
                        }),
                      );
                      void client.invalidateQueries({ queryKey: appKeys.all });
                    }
                    setTotal(c);
                  }}
                />
                total
              </span>
            </span>
          )
        }
      >
        {(app || providers.data) && (
          <div
            className="flex flex-wrap items-center gap-3 text-fg-muted-regular-small"
            style={{ minHeight: "var(--item-height)" }}
          >
            {app && (
              <StatusChip tone={statusLabel(app).tone}>
                {statusLabel(app).text}
              </StatusChip>
            )}
            {providers.data && (
              <span>{searchesLine(providers.data.searches)}</span>
            )}
            {app && app.status !== "setup" && <span>{app.schedule_label}</span>}
          </div>
        )}
        {step !== "start" && (
          <TabToggle
            ariaLabel="Steps"
            value={step}
            onChange={setStep}
            options={STEPS.map((s) => ({
              value: s.step,
              label:
                s.step === "questions" && app && app.open_questions > 0 ? (
                  <span className="inline-flex items-center gap-1">
                    {s.label}
                    <NumberCount count={app.open_questions} size="sm" />
                  </span>
                ) : (
                  s.label
                ),
              disabled:
                (!app && s.step !== "setup") ||
                (s.step === "runs" && app?.status === "setup"),
            }))}
          />
        )}
      </AppPageHead>

      {step === "start" && (
        <StartStep
          orgId={orgId}
          onDesign={fromDesign}
          onTables={() => setStep("setup")}
        />
      )}

      {step === "setup" && (
        <div className="flex flex-col gap-3 min-w-0">
          <SectionRule label="Brief" />
          <Composer
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            aria-label="Brief"
            placeholder="What to find, where to look, and what counts as a source"
            bar={
              <>
                {providers.data && choice && (
                  <ModelChip
                    catalog={providers.data}
                    org={org}
                    choice={choice}
                    onPick={(provider, model) => setPicked({ provider, model })}
                    menuOpen={modelMenu}
                    onMenuOpen={setModelMenu}
                  />
                )}
                <span className="flex-1" />
                {running && edited && (
                  <>
                    <span className="text-fg-muted-regular-small">
                      {problem}
                    </span>
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        if (!app) return;
                        setBrief(app.brief);
                        setLent(lentOf(app));
                        setPicked({ provider: app.provider, model: app.model });
                        setPicking(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      disabled={Boolean(problem) || busy}
                      loading={busy}
                      onClick={saveSetup}
                    >
                      Save
                    </Button>
                  </>
                )}
              </>
            }
          />
          <SectionRule
            label="Tables"
            end={
              <IconPicker
                ariaLabel="Show the tables as"
                options={[
                  { value: "list", label: "List", Icon: TableRowsOutlinedIcon },
                  { value: "graph", label: "Graph", Icon: HubOutlinedIcon },
                ]}
                value={tablesView}
                onChange={setTablesView}
              />
            }
          />
          {/* One height for both views, so switching never moves the page. */}
          <div className="flex flex-col" style={{ height: "22rem" }}>
            {tablesView === "graph" ? (
              <LentGraph
                lent={lent}
                onLend={(t) =>
                  !lent.some((x) => x.table_id === t.table_id) &&
                  setLent([...lent, t])
                }
              />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
                <LentList lent={lent} onChange={setLent} />
                <span className="text-fg-muted-regular-small">
                  The app can only add, soft-delete or edit rows on the tables
                  you give write access to.
                </span>
                {picking ? (
                  <TablePicker
                    orgId={orgId}
                    lent={lent}
                    onAdd={(added) => {
                      setLent([...lent, ...added]);
                      setPicking(false);
                    }}
                    onCancel={lent.length ? () => setPicking(false) : undefined}
                  />
                ) : (
                  <Button
                    variant="secondary"
                    className="self-start"
                    icon={<AddIcon className="icon-sm" />}
                    onClick={() => setPicking(true)}
                  >
                    Add tables
                  </Button>
                )}
              </div>
            )}
          </div>
          {!running && (
            <div className="flex items-center justify-end gap-3">
              <span className="text-fg-muted-regular-small">{problem}</span>
              <Button
                variant="primary"
                disabled={Boolean(problem) || busy}
                loading={busy}
                onClick={saveSetup}
              >
                Continue
              </Button>
            </div>
          )}
          {!app && (
            <span className="text-fg-muted-regular-small">
              Next, the agent reads the tables, runs a few queries and asks up
              to three rounds of questions. Your answers stay with the brief.
            </span>
          )}
          <RefusalCallout error={failure} />
          {app && (
            <Button
              variant="quiet-link"
              size="sm"
              className="self-start text-error"
              onClick={() => setConfirmDelete(true)}
            >
              Delete app
            </Button>
          )}
        </div>
      )}

      {step === "questions" && app && (
        <ClarificationsPane
          app={app}
          onDone={() => setStep("schedule")}
          onPolicy={(policy) =>
            act(() =>
              updateApp(app.app_id, setupOf(app, { policy })).then(() => {}),
            )
          }
        />
      )}

      {step === "schedule" && app && (
        <ScheduleStep
          app={app}
          total={total}
          busy={busy}
          failure={failure}
          onSave={(runs) =>
            act(async () => {
              await updateApp(
                app.app_id,
                setupOf(app, { ...runs, cost_limit_cents: total }),
              );
              if (app.status === "setup") {
                await startApp(app.app_id);
                setStep("runs");
              }
            })
          }
        />
      )}

      {step === "runs" && app && (
        <RunsTab app={app} onAnswer={() => setStep("questions")} />
      )}

      {app && (
        <ConfirmDialog
          open={confirmDelete}
          title={`Delete ${app.name}?`}
          message="The app stops. The tables lent to it stay where they are, with every row it added."
          confirmText="Delete app"
          confirmVariant="destructive"
          isLoading={remove.isPending}
          onConfirm={() =>
            remove.mutate(undefined, { onSuccess: () => navigate("/apps") })
          }
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </AppPageShell>
  );
}

/** Before the name: start from a design, its tables and brief carried
 *  over, or from tables picked one by one. */
function StartStep({
  orgId,
  onDesign,
  onTables,
}: {
  orgId: string | null;
  onDesign: (design: DesignApp) => void;
  onTables: () => void;
}) {
  const designs = (useApps(orgId).data ?? [])
    .filter((a): a is DesignSummary => a.kind === "design")
    .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
  const applied = designs.filter((d) => d.applied_version);
  const [fromDesign, setFromDesign] = useState(applied.length > 0);
  const [chosen, setChosen] = useState<string | null>(
    applied[0]?.app_id ?? null,
  );
  const design = useDesignApp(fromDesign ? chosen : null).data;
  return (
    <div className="flex flex-col gap-4">
      <div
        role="radiogroup"
        aria-label="Start from"
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(16rem, 1fr))" }}
      >
        <OptionCard
          selected={fromDesign}
          icon={<WidgetsOutlinedIcon sx={{ fontSize: 18 }} />}
          title="Start from a design"
          detail="Its tables and brief carry over"
          onSelect={() => setFromDesign(true)}
        />
        <OptionCard
          selected={!fromDesign}
          icon={<EditNoteOutlinedIcon sx={{ fontSize: 18 }} />}
          title="Provide tables"
          detail="Pick them from any model"
          onSelect={() => setFromDesign(false)}
        />
      </div>
      {fromDesign && (
        <section className="flex flex-col gap-2">
          <SectionRule label="Applied designs" />
          {designs.length === 0 && (
            <span className="text-fg-muted-regular-small">
              No design apps yet
            </span>
          )}
          {designs.length > 0 && (
            <ul
              role="radiogroup"
              aria-label="Design"
              className="flex flex-col rounded-app border border-border bg-bg-subtle m-0 p-1"
            >
              {designs.map((d) => {
                const on = chosen === d.app_id;
                return (
                  <li key={d.app_id} className="list-none">
                    {/* eslint-disable-next-line no-restricted-syntax -- a radio row, as the design form's table list draws it */}
                    <button
                      type="button"
                      role="radio"
                      aria-checked={on}
                      disabled={!d.applied_version}
                      onClick={() => setChosen(d.app_id)}
                      className={`flex w-full items-center gap-2 rounded-app px-2 text-left enabled:hover:bg-bg-hover cursor-pointer disabled:cursor-not-allowed disabled:text-fg-muted ${on ? "bg-list-selected-light" : ""}`}
                      style={{ minHeight: "var(--item-height)" }}
                    >
                      <Radio selected={on} />
                      <WidgetsOutlinedIcon
                        sx={{ fontSize: 14 }}
                        className="text-fg-muted"
                      />
                      <span className="flex-1 truncate">{d.name}</span>
                      <span className="text-mono-small-muted">
                        {d.applied_version
                          ? `v${d.applied_version} applied · ${whenLabel(d.updated_at)}`
                          : "not applied"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={fromDesign && !design}
          onClick={() => (fromDesign && design ? onDesign(design) : onTables())}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

const CADENCES: { value: AppCadence; title: string; detail: string }[] = [
  { value: "once", title: "Once", detail: "Right after you start it" },
  { value: "daily", title: "Daily", detail: "Every day at one time" },
  { value: "weekly", title: "Weekly", detail: "On the days you pick" },
  { value: "monthly", title: "Monthly", detail: "On the 1st of the month" },
];

/** The person's own settings: when runs happen, and how far each may go. */
function ScheduleStep({
  app,
  total,
  busy,
  failure,
  onSave,
}: {
  app: AppDetail;
  total: number;
  busy: boolean;
  failure: unknown;
  onSave: (runs: Partial<AppSetup>) => void;
}) {
  const [cadence, setCadence] = useState<AppCadence>(app.cadence);
  const [days, setDays] = useState<string[]>(
    app.days.length ? app.days : ["Mon"],
  );
  const [clock, setClock] = useState(CLOCK.parse(app.run_time) ?? 6 * 60);
  const [zone, setZone] = useState(app.time_zone);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [minutes, setMinutes] = useState(app.run_minutes);
  const [runCap, setRunCap] = useState(Math.min(app.run_cap_cents, total));
  const problem = runsProblem({
    cadence,
    days,
    runCapCents: runCap,
    totalCents: total,
  });
  const time = CLOCK.show(clock);
  const fit = Math.floor(total / Math.max(runCap, 1));
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <SectionRule label="When it runs" />
        <div
          role="radiogroup"
          aria-label="How often"
          className="grid gap-2"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))",
          }}
        >
          {CADENCES.map((c) => (
            <OptionCard
              key={c.value}
              selected={cadence === c.value}
              onSelect={() => setCadence(c.value)}
              title={c.title}
              detail={c.detail}
            />
          ))}
        </div>
        {cadence !== "once" && (
          <Card variant="subtle">
            <div className="flex flex-col gap-3">
              {cadence === "weekly" && (
                <div
                  className="flex flex-wrap items-center gap-1.5"
                  role="group"
                  aria-label="Days"
                >
                  {WEEKDAYS.map((day) => {
                    const on = days.includes(day);
                    return (
                      // eslint-disable-next-line no-restricted-syntax -- a day toggle, round like a calendar's
                      <button
                        key={day}
                        type="button"
                        aria-pressed={on}
                        aria-label={DAY_NAMES[day]}
                        onClick={() =>
                          setDays(
                            WEEKDAYS.filter((d) =>
                              d === day ? !on : days.includes(d),
                            ),
                          )
                        }
                        className={`flex items-center justify-center rounded-full border text-small cursor-pointer ${on ? "border-accent bg-accent text-on-accent" : "border-border text-fg-muted hover:bg-bg-hover"}`}
                        style={{ width: "2rem", height: "2rem" }}
                      >
                        {day.slice(0, 2)}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex flex-wrap items-baseline gap-1.5 text-fg">
                <span>At</span>
                <InlineFigure
                  value={clock}
                  label="run time"
                  format={CLOCK}
                  onSave={async (v) => {
                    if (v !== null) setClock(v);
                  }}
                />
                {zoneOpen ? (
                  <TextInput
                    value={zone}
                    onChange={setZone}
                    onBlur={() => setZoneOpen(false)}
                    ariaLabel="Time zone"
                    autoFocus
                    className="w-48"
                  />
                ) : (
                  <Button
                    variant="quiet-link"
                    size="sm"
                    onClick={() => setZoneOpen(true)}
                  >
                    {zone} time
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}
        <span className="text-fg-muted">
          {scheduleSentence(cadence, days, time, zone)}
        </span>
      </section>

      <section className="flex flex-col gap-3">
        <SectionRule label="Each run" />
        <p className="m-0 flex flex-wrap items-baseline gap-1.5 text-fg">
          <span>Stops after</span>
          <InlineFigure
            value={minutes}
            label="minutes per run"
            format={MINUTES}
            refuse={(n) =>
              n < 1 || n > MAX_RUN_MINUTES
                ? `Between 1 and ${MAX_RUN_MINUTES}`
                : null
            }
            onSave={async (v) => {
              if (v !== null) setMinutes(v);
            }}
          />
          <span>or</span>
          <InlineFigure
            value={runCap}
            label="cost per run"
            refuse={(c) =>
              c < 1 || c > total ? `Between $0.01 and ${usd(total)}` : null
            }
            onSave={async (c) => {
              if (c !== null) setRunCap(c);
            }}
          />
          <span>on tokens and web lookups, whichever comes first.</span>
        </p>
        <span className={problem ? "text-error" : "text-fg-muted"}>
          {problem ??
            `At most ${fit === 1 ? "1 run fits" : `${fit} runs fit`} in the app's ${usd(total)} total.`}
        </span>
      </section>

      <RefusalCallout error={failure} />
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={Boolean(problem) || busy}
          loading={busy}
          onClick={() =>
            onSave({
              cadence,
              days: cadence === "weekly" ? days : [],
              run_time: time,
              time_zone: zone.trim(),
              run_minutes: minutes,
              run_cap_cents: runCap,
            })
          }
        >
          {app.status === "setup" ? "Start research" : "Save"}
        </Button>
      </div>
    </div>
  );
}
